import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Typography,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TablePagination,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Receipt,
  LocalGasStation,
  Build,
  Restaurant,
  AttachMoney,
} from '@mui/icons-material';
import api from '../../services/api';

/**
 * Componente de Gestión de Gastos
 * Path: backoffice-web/src/pages/Gastos/Gastos.js
 * Última modificación: 2025-01-02 06:15
 * Dev: cpalacios01@gmail.com | +595994648273
 */

export default function Gastos() {
  const [gastos, setGastos] = useState([]);
  const [filteredGastos, setFilteredGastos] = useState([]);
  const [redondosAbiertos, setRedondosAbiertos] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGasto, setEditingGasto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [conceptoFilter, setConceptoFilter] = useState('todos');
  const [redondoFilter, setRedondoFilter] = useState('todos');
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Formulario
  const [formData, setFormData] = useState({
    redondo_id: '',
    concepto: '',
    monto: '',
    comprobante: '',
    descripcion: '',
  });
  
  useEffect(() => {
    fetchGastos();
    fetchRedondosAbiertos();
    fetchConceptos();
  }, []);
  
  useEffect(() => {
    applyFilters();
  }, [gastos, searchTerm, conceptoFilter, redondoFilter]);
  
  const fetchGastos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/gastos');
      // Manejar diferentes formatos de respuesta
      const data = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      setGastos(data);
    } catch (error) {
      showSnackbar('Error al cargar gastos', 'error');
      console.error('Error:', error);
      setGastos([]); // Asegurar que siempre sea un array
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRedondosAbiertos = async () => {
    try {
      const response = await api.get('/redondos/abiertos');
      // Manejar diferentes formatos de respuesta
      const data = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      setRedondosAbiertos(data);
    } catch (error) {
      console.error('Error al cargar redondos:', error);
      setRedondosAbiertos([]); // Asegurar que siempre sea un array
    }
  };
  
  const fetchConceptos = async () => {
    try {
      const response = await api.get('/gastos/conceptos');
      // Manejar diferentes formatos de respuesta
      const data = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      setConceptos(data);
    } catch (error) {
      console.error('Error al cargar conceptos:', error);
      setConceptos([]); // Asegurar que siempre sea un array
    }
  };
  
  const applyFilters = () => {
    let filtered = [...gastos];
    
    // Filtro por búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(gasto =>
        gasto.descripcion?.toLowerCase().includes(search) ||
        gasto.comprobante?.toLowerCase().includes(search) ||
        gasto.concepto?.toLowerCase().includes(search)
      );
    }
    
    // Filtro por concepto
    if (conceptoFilter !== 'todos') {
      filtered = filtered.filter(gasto => gasto.concepto === conceptoFilter);
    }
    
    // Filtro por redondo
    if (redondoFilter !== 'todos') {
      filtered = filtered.filter(gasto => gasto.redondo_id === parseInt(redondoFilter));
    }
    
    setFilteredGastos(filtered);
    setPage(0);
  };
  
  const handleOpenDialog = (gasto = null) => {
    if (gasto) {
      setEditingGasto(gasto);
      setFormData({
        redondo_id: gasto.redondo_id,
        concepto: gasto.concepto,
        monto: gasto.monto,
        comprobante: gasto.comprobante || '',
        descripcion: gasto.descripcion || '',
      });
    } else {
      setEditingGasto(null);
      setFormData({
        redondo_id: '',
        concepto: '',
        monto: '',
        comprobante: '',
        descripcion: '',
      });
    }
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGasto(null);
    setFormData({
      redondo_id: '',
      concepto: '',
      monto: '',
      comprobante: '',
      descripcion: '',
    });
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async () => {
    try {
      // Validación
      if (!formData.redondo_id || !formData.concepto || !formData.monto) {
        showSnackbar('Redondo, concepto y monto son requeridos', 'warning');
        return;
      }
      
      setLoading(true);
      
      const dataToSend = {
        redondo_id: formData.redondo_id,
        concepto: formData.concepto,
        monto: parseFloat(formData.monto),
        comprobante: formData.comprobante.trim() || null,
        descripcion: formData.descripcion.trim() || null,
      };
      
      if (editingGasto) {
        await api.put(`/gastos/${editingGasto.id}`, dataToSend);
        showSnackbar('Gasto actualizado exitosamente', 'success');
      } else {
        await api.post('/gastos', dataToSend);
        showSnackbar('Gasto registrado exitosamente', 'success');
      }
      
      handleCloseDialog();
      fetchGastos();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || 'Error al guardar gasto',
        'error'
      );
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este gasto?')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.delete(`/gastos/${id}`);
      showSnackbar('Gasto eliminado exitosamente', 'success');
      fetchGastos();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || 'Error al eliminar gasto',
        'error'
      );
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const formatMonto = (monto) => {
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      minimumFractionDigits: 0,
    }).format(monto);
  };
  
  const getConceptoIcon = (concepto) => {
    switch (concepto) {
      case 'combustible':
        return <LocalGasStation fontSize="small" />;
      case 'reparacion':
        return <Build fontSize="small" />;
      case 'comida':
        return <Restaurant fontSize="small" />;
      default:
        return <AttachMoney fontSize="small" />;
    }
  };
  
  const getConceptoColor = (concepto) => {
    switch (concepto) {
      case 'combustible':
        return 'warning';
      case 'reparacion':
        return 'error';
      case 'comida':
        return 'info';
      case 'peaje':
        return 'secondary';
      default:
        return 'default';
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt />
          Gestión de Gastos
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={loading || redondosAbiertos.length === 0}
        >
          Registrar Gasto
        </Button>
      </Box>
      
      {/* Alert si no hay redondos abiertos */}
      {redondosAbiertos.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No hay redondos abiertos. Debe abrir un redondo antes de registrar gastos.
        </Alert>
      )}
      
      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Buscar por descripción, comprobante..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Concepto</InputLabel>
              <Select
                value={conceptoFilter}
                onChange={(e) => setConceptoFilter(e.target.value)}
                label="Concepto"
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                }
              >
                <MenuItem value="todos">Todos</MenuItem>
                {conceptos.map((concepto) => (
                  <MenuItem key={concepto} value={concepto}>
                    {concepto.charAt(0).toUpperCase() + concepto.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Redondo</InputLabel>
              <Select
                value={redondoFilter}
                onChange={(e) => setRedondoFilter(e.target.value)}
                label="Redondo"
              >
                <MenuItem value="todos">Todos</MenuItem>
                {redondosAbiertos.map((redondo) => (
                  <MenuItem key={redondo.id} value={redondo.id}>
                    {redondo.codigo_unico}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              {filteredGastos.length} gasto(s)
            </Alert>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Redondo</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Concepto</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Comprobante</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Monto</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Cargando gastos...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredGastos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography color="textSecondary">
                    No se encontraron gastos
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredGastos
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((gasto) => (
                  <TableRow
                    key={gasto.id}
                    hover
                    sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(gasto.fecha_hora).toLocaleDateString('es-PY')}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(gasto.fecha_hora).toLocaleTimeString('es-PY')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={gasto.redondo?.codigo_unico || 'N/A'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getConceptoIcon(gasto.concepto)}
                        label={gasto.concepto.charAt(0).toUpperCase() + gasto.concepto.slice(1)}
                        color={getConceptoColor(gasto.concepto)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {gasto.comprobante ? (
                        <Chip label={gasto.comprobante} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Sin comprobante
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {gasto.descripcion || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="error">
                        {formatMonto(gasto.monto)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(gasto)}
                        title="Editar"
                        disabled={gasto.redondo?.estado !== 'abierto'}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(gasto.id)}
                        title="Eliminar"
                        disabled={loading || gasto.redondo?.estado !== 'abierto'}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredGastos.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </TableContainer>
      
      {/* Dialog Crear/Editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingGasto ? 'Editar Gasto' : 'Registrar Nuevo Gasto'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Redondo</InputLabel>
                <Select
                  name="redondo_id"
                  value={formData.redondo_id}
                  onChange={handleChange}
                  label="Redondo"
                  disabled={editingGasto}
                >
                  {redondosAbiertos.map((redondo) => (
                    <MenuItem key={redondo.id} value={redondo.id}>
                      {redondo.codigo_unico} - {redondo.chofer?.nombre || redondo.chofer?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Concepto</InputLabel>
                <Select
                  name="concepto"
                  value={formData.concepto}
                  onChange={handleChange}
                  label="Concepto"
                >
                  {conceptos.map((concepto) => (
                    <MenuItem key={concepto} value={concepto}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getConceptoIcon(concepto)}
                        {concepto.charAt(0).toUpperCase() + concepto.slice(1)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Monto"
                name="monto"
                type="number"
                value={formData.monto}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">Gs.</InputAdornment>,
                }}
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nº Comprobante"
                name="comprobante"
                value={formData.comprobante}
                onChange={handleChange}
                placeholder="Ej: FAC-001234"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Detalles adicionales del gasto..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {editingGasto ? 'Actualizar' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// cpalacios01@gmail.com | +595994648273
